import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
    Checkbox, Divider, ListItem, Box, Typography, List, IconButton,
    ListItemText, ListItemButton, Dialog, Slide, FormControlLabel, Button, Stack
} from "@mui/material";
import { PAContext } from "../../../Services/PAContext";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import SupplyItem from "../../../MasterEventObjects/SupplyItem";
import { capitalizeFirstLetter } from "../../Helpers/TextStyling";
import InfoSupplyEssentials from "../../Dialogs/InfoSupplyEssentials";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function SuppliesAddEssentialsDialog({ open, onClose, selectedAttendeeId }) {
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account } = dataArray;

    const [selectedEssentials, setSelectedEssentials] = useState([]);
    const [infoOpen, setInfoOpen] = useState(false);

    // 1. Filter out essentials already in the event
    const existingEssentialIds = useMemo(() => (PA_GlobalObject.Planning.supplies || [])
        .filter(item => item.essential === true && item.essential_id)
        .map(item => item.essential_id), [PA_GlobalObject.Planning.supplies]);

    const availableEssentials = useMemo(() => (Account.essentials ?? []).filter(
        (essential) => !existingEssentialIds.includes(essential.id)
    ), [Account.essentials, existingEssentialIds]);

    // 2. Group available essentials
    const groupedEssentials = useMemo(() => availableEssentials.reduce((acc, item) => {
        const subCat = item.sub_category || "Uncategorized";
        if (!acc[subCat]) acc[subCat] = [];
        acc[subCat].push(item);
        return acc;
    }, {}), [availableEssentials]);

    // Reset selection when opening
    useEffect(() => {
        if (open) setSelectedEssentials([]);
    }, [open]);

    const handleToggleAll = () => {
        if (selectedEssentials.length === availableEssentials.length) {
            setSelectedEssentials([]);
        } else {
            setSelectedEssentials([...availableEssentials]);
        }
    };

    const toggleEssential = (item) => {
        setSelectedEssentials((prev) =>
            prev.some((s) => s.id === item.id)
                ? prev.filter((s) => s.id !== item.id)
                : [...prev, item]
        );
    };

    const handleAddSelected = () => {
        if (selectedEssentials.length === 0) return;
        const currentUserId = selectedAttendeeId || Account.id || "0";

        const newSupplyItems = selectedEssentials.map((essential) => {
            const item = new SupplyItem();
            item.name = essential.name;
            item.category = essential.category || 'supplies';
            item.sub_category = essential.sub_category;
            item.private = essential.private || false;
            item.assigned_by = Account.id;
            item.assigned_to = currentUserId;
            item.essential = true;
            item.essential_id = essential.id;
            item.notes = essential.notes || "";
            item.status = 1;
            return item;
        });

        set_PA_GlobalObject((prev) => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                supplies: [...(prev.Planning.supplies || []), ...newSupplyItems],
            },
        }));

        if (PA_GlobalObject.Planning.status !== 10) {
            const idPairs = newSupplyItems.map(item => ({
                essential_id: item.essential_id,
                supply_id: item.id
            }));

            sendSocketMessage({
                action: "E_addEssentialSupplyItems",
                event_id: PA_GlobalObject.Planning.id,
                id_pairs: idPairs
            });
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '20px', backgroundColor: '#fff', overflow: 'hidden' } }}
        >
            {/* COMMON HEADER */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f3f5' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#151269' }}>Essentials</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#95a5a6', letterSpacing: 0.5 }}>PERSONAL GEAR VAULT</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#e74c3c', backgroundColor: 'rgba(231, 76, 60, 0.1)', '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.2)' } }}>
                    <i className="icon-cross" />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: "auto", bgcolor: '#f8f9fa' }}>
                {availableEssentials.length > 0 ? (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#7f8c8d' }}>
                                SELECT ITEMS TO BRING
                            </Typography>
                            <FormControlLabel
                                control={<Checkbox size="small" checked={selectedEssentials.length === availableEssentials.length} onChange={handleToggleAll} />}
                                label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Select All</Typography>}
                            />
                        </Box>

                        {Object.entries(groupedEssentials)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([category, items]) => (
                                <Box key={category} sx={{ mb: 3 }}>
                                    <Typography variant="overline" sx={{ color: '#27ae60', fontWeight: 900, px: 1, letterSpacing: 1 }}>
                                        {category}
                                    </Typography>
                                    <List disablePadding sx={{ background: '#fff', borderRadius: '16px', border: '1px solid #eef0f2', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        {items.map((item, index) => {
                                            const isSelected = selectedEssentials.some((s) => s.id === item.id);
                                            return (
                                                <React.Fragment key={item.id}>
                                                    <ListItem
                                                        disablePadding
                                                        secondaryAction={<Checkbox edge="end" checked={isSelected} onChange={() => toggleEssential(item)} />}
                                                    >
                                                        <ListItemButton onClick={() => toggleEssential(item)} sx={{ py: 1.5 }}>
                                                            <ListItemText
                                                                primary={capitalizeFirstLetter(item.name)}
                                                                primaryTypographyProps={{ sx: { fontWeight: isSelected ? 800 : 500, color: isSelected ? '#151269' : '#2c3e50', fontSize: '15px' } }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                    {index < items.length - 1 && <Divider />}
                                                </React.Fragment>
                                            );
                                        })}
                                    </List>
                                </Box>
                            ))}
                    </>
                ) : (
                    <Box sx={{ textAlign: "center", py: 10 }}>
                        <Box sx={{ mb: 2, display: 'inline-flex', p: 3, borderRadius: '50%', bgcolor: '#e8f5e9' }}>
                            <i className="icon-check-circle" style={{ fontSize: '40px', color: '#4caf50' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2c3e50' }}>
                            All caught up!
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                            All your vault essentials are already added to this event.
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* COMMON FOOTER */}
            <Box sx={{ p: 2, borderTop: '1px solid #f1f3f5', display: 'flex', gap: 2, bgcolor: '#fff' }}>
                <Button fullWidth onClick={onClose} sx={{ py: 1.5, borderRadius: '12px', fontWeight: 800, color: '#95a5a6', bgcolor: '#f8f9fa' }}>
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddSelected}
                    disabled={selectedEssentials.length === 0}
                    sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        fontWeight: 800,
                        bgcolor: '#151269',
                        '&:hover': { bgcolor: '#1a1780' },
                        boxShadow: '0 4px 14px rgba(21, 18, 105, 0.25)',
                        '&.Mui-disabled': { bgcolor: '#bdc3c7', color: '#fff' }
                    }}
                >
                    {selectedEssentials.length === 0 ? 'Add Items' : `Add ${selectedEssentials.length} to Event`}
                </Button>
            </Box>

            <InfoSupplyEssentials isOpen={infoOpen} onClose={() => setInfoOpen(false)} />
        </Dialog>
    );
}

export default SuppliesAddEssentialsDialog;